import { pushRoute } from '../../lib/router.js';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Col,
  DropdownButton,
  FormControl,
  Grid,
  Row,
} from 'react-bootstrap';
import CustomMenuItem from '../../components/CustomMenuItem.jsx';
import Empty from '../../components/Empty.jsx';
import { selectUser } from '../../modules/auth.js';
import {
  hideOrgStructureUploadError,
  retrieveOrganization,
  selectOrganization,
  selectRetrieveOrganizationState,
} from '../../modules/organizations.js';
import OrganizationHeader from '../../components/headers/PageHeaders/Organization/OrganizationHeader.jsx';
import { useFetchFile } from '../../hooks/useFetchFile.js';
import { orgStructureContent } from '../../components/EmptyStates.jsx';
import SubHeader from '../../components/SubHeader.jsx';
import { PlusButtonIcon } from '../../components/icons/PlusButtonIcon.jsx';
import AuthorizedAction from '../../components/AuthorizedAction.js';
import GroupsTree from '../../components/organizations/GroupsTree.jsx';
import { EditBlueIcon } from '../../components/icons/EditBlueIcon.jsx';
import { EditOrganizationLink } from '../../components/links/index.jsx';
import DownloadReportIcon from '../../components/icons/DownloadReportIcon.jsx';
import {
  retrieveOrgGroups,
  searchOrgGroups,
  selectOrgGroups,
  selectRetrieveGroupsState,
} from '../../modules/orgGroups.js';
import { getOrgSlug } from '../../lib/utils.js';

const mapStateToProps = (state) => {
  return {
    organization: selectOrganization(state),
    groups: selectOrgGroups(state),
    retrieveError: selectRetrieveOrganizationState(state).error,
    retrieveOrganizationSuccess: selectRetrieveOrganizationState(state).success,
    retrieveGroupsSuccess: selectRetrieveGroupsState(state).success,
    user: selectUser(state),
    apiUrl: state.config.apiUrl,
  };
};

const mapDispatchToProps = {
  retrieveOrganization,
  retrieveOrgGroups,
  searchOrgGroups,
  hideOrgStructureUploadError,
};

function OrganizationDetailContainer({
  user,
  organization,
  groups,
  retrieveOrganizationSuccess,
  hideOrgStructureUploadError,
  retrieveOrgGroups,
  retrieveOrganization,
  searchOrgGroups,
  apiUrl,
}) {
  const slug = getOrgSlug();
  const { loading, downloadBlob } = useFetchFile();
  const [showEmpty, setShowEmpty] = useState(false);

  const onHideError = () => {
    if (!window.confirm('Are you sure you want to hide this error message?'))
      return;
    hideOrgStructureUploadError(organization.id).then(() =>
      retrieveOrganization(organization.id)
    );
  };

  const onDownloadCsv = () => {
    downloadBlob(
      apiUrl,
      `organizations/${organization.id}/group_structure_csv`,
      null,
      'current-group-structure CSV',
      'csv'
    );
  };

  const onDownloadErrorsCsv = () => {
    downloadBlob(
      apiUrl,
      `organizations/${organization.id}/group_structure_errors_csv`,
      null,
      'errors-organization-structure CSV',
      'csv'
    );
  };

  const handleSearch = (event) => {
    const query = event.target.value;
    searchOrgGroups({ active: true, search: query });
  };

  useEffect(() => {
    if (user) {
      retrieveOrganization(user.organizationId);
      retrieveOrgGroups({ active: true }).then(
        ({ result }) => !result.length && setShowEmpty(true)
      );
    }
  }, [user]);

  return (
    <React.Fragment>
      <OrganizationHeader organizationTabs slug={slug} />

      {retrieveOrganizationSuccess && (
        <React.Fragment>
          <SubHeader small flexJustify>
            <h2 className="flex">
              {`${organization.name} Organization Structure`}
            </h2>

            <div className="flex flex-end" style={{ width: '50%' }}>
              {showEmpty ? (
                <AuthorizedAction action="create" subject="group">
                  <Button
                    bsStyle="success"
                    className="add-activity-button"
                    onClick={() => pushRoute('organization/edit')}
                  >
                    <PlusButtonIcon />
                    Create Organization Structure
                  </Button>
                </AuthorizedAction>
              ) : (
                <React.Fragment>
                  <div
                    className="m-r-16 m-l-12"
                    style={{ width: '100%', maxWidth: '540px' }}
                  >
                    <FormControl
                      placeholder="Search"
                      type="text"
                      onChange={handleSearch}
                    />
                  </div>
                  <div className="actions">
                    <DropdownButton
                      noCaret
                      title=""
                      id="id"
                      className="btn-more btn btn-default"
                    >
                      {organization?.canManageCustomSettings ? (
                        <EditOrganizationLink>
                          <CustomMenuItem>
                            <span className="flex flex-align-center">
                              <EditBlueIcon />
                              <span className="m-l-8">Edit</span>
                            </span>
                          </CustomMenuItem>
                          <CustomMenuItem divider />
                        </EditOrganizationLink>
                      ) : null}

                      {groups.length ? (
                        <CustomMenuItem>
                          <button
                            type="button"
                            className="flex flex-align-center w-100 h-100 p-0 btn-clear bg-transparent"
                            disabled={loading}
                            onClick={onDownloadCsv}
                          >
                            <DownloadReportIcon />
                            <span className="m-l-8">
                              {loading ? 'Loading...' : 'Download CSV'}
                            </span>
                          </button>
                        </CustomMenuItem>
                      ) : null}
                    </DropdownButton>
                  </div>
                </React.Fragment>
              )}
            </div>
          </SubHeader>

          {organization &&
          organization.groupsStructureErrors &&
          organization.groupsStructureErrors.error ? (
            <div className="component-xl component-border-bottom component-bg-white text-center">
              <div className="flex flex-end w-100">
                <button
                  className="btn btn-default btn-cross"
                  onClick={onHideError}
                >
                  close
                </button>
              </div>
              <h3 className="flex text-danger flex-center m-b-12">
                There was an issue with the CSV.
              </h3>
              <AuthorizedAction
                action="update"
                subject="organizationStakeholder"
              >
                <p className="no-margin text-muted">
                  Please download the CSV error report
                  <button
                    type="button"
                    className="btn-link"
                    onClick={onDownloadErrorsCsv}
                  >
                    here
                  </button>
                </p>
                <p className="text-muted">
                  Changes implemented? If everything is correct, feel free to
                  dismiss this alert — it won’t show up again.
                </p>
              </AuthorizedAction>
            </div>
          ) : null}

          {!showEmpty ? (
            <Grid>
              <Row>
                <Col sm={12}>
                  <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
                    <Row>
                      <Col sm={10} smOffset={1}>
                        <GroupsTree
                          rootLabel={organization ? organization.name : ''}
                          groups={groups}
                        />
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            </Grid>
          ) : (
            <div className="component-xl component-bg-white">
              <Empty
                content={orgStructureContent}
                action={() => pushRoute('organization/edit')}
                addBtn
              />
            </div>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OrganizationDetailContainer);
